import { withAlpha, comboTier } from '../../piano/render/pianoRenderColour';
import {
  COMBO_BLOOM_PALETTE,
  COMBO_HUD_BASE_FONT_PX,
  COMBO_UNDERLINE_MIN,
  HP_BAR_HEIGHT_PX,
  HP_BAR_TOP_PX,
  HP_DANGER_PULSE_THRESHOLD,
  HP_TICK_DIVISIONS,
  HP_TICK_THICKNESS_PX,
} from './guitarRenderConstants';
import type { GuitarLayout } from '../guitarPlayfieldGeometry';
import type { GuitarRenderInput } from './guitarRenderTypes';
import type { RenderQuality } from '../../rendering/renderQuality';

/**
 * HP bar + score + combo readout for Guitar mode. Mirrors the Piano
 * HUD's behaviour and visual rhythm so the muscle memory of "score
 * lives top-right" carries between modes; the only divergence is the
 * lateral anchor (top of the perspective trapezoid rather than the
 * flat lane stack).
 */
export class GuitarHudRenderer {
  private comboPulseT = 0;
  private lastShownCombo = 0;

  reset(): void {
    this.comboPulseT = 0;
    this.lastShownCombo = 0;
  }

  advance(currentCombo: number, dt: number): void {
    if (currentCombo > this.lastShownCombo) {
      this.comboPulseT = 1;
    }
    this.lastShownCombo = currentCombo;
    if (dt > 0) {
      const decayRate = 4;
      this.comboPulseT = Math.max(0, this.comboPulseT - decayRate * dt);
    }
  }

  draw(
    ctx: CanvasRenderingContext2D,
    input: GuitarRenderInput,
    layout: GuitarLayout,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    const { chart, state, displayedScore } = input;
    const palette = chart.palette;

    // HP bar across the top of the playing field. We anchor to the
    // bottom-stack (foreground) width so the bar stays the same on
    // any resize regardless of the trapezoid's far-end width — the
    // HUD reads at the strum-bar scale, not the highway scale.
    const hpFrac = Math.max(0, Math.min(1, state.hp / state.maxHp));
    const barX = layout.centreX - layout.bottomStackWidth / 2;
    const barW = layout.bottomStackWidth;
    ctx.fillStyle = withAlpha(palette.bgAccent, 0.85);
    ctx.fillRect(barX, HP_BAR_TOP_PX, barW, HP_BAR_HEIGHT_PX);

    const lowHp = hpFrac <= 0.4;
    const danger = hpFrac <= HP_DANGER_PULSE_THRESHOLD;
    const dangerPulse =
      danger && !reducedMotion ? 0.5 + 0.5 * Math.sin(performance.now() * 0.015) : 0;
    ctx.save();
    if (danger && quality.chromeShadows) {
      ctx.shadowColor = palette.hazard;
      ctx.shadowBlur = reducedMotion ? 10 : 8 + dangerPulse * 18;
    }
    ctx.fillStyle = lowHp ? palette.hazard : palette.padPerfect;
    ctx.fillRect(barX, HP_BAR_TOP_PX, barW * hpFrac, HP_BAR_HEIGHT_PX);
    ctx.restore();

    // Quarter-bar tick marks — three small vertical notches at 25 /
    // 50 / 75 %. Reads as a milestone scale so the player has a
    // glanceable sense of "I'm three-quarters full" without parsing
    // raw width. Brighter when a tick has been "reached" (hpFrac
    // crossed it) so the bar feels like a filling battery.
    for (let i = 1; i < HP_TICK_DIVISIONS; i += 1) {
      const tickFrac = i / HP_TICK_DIVISIONS;
      const tickX = barX + barW * tickFrac;
      const reached = hpFrac >= tickFrac;
      ctx.fillStyle = withAlpha('#ffffff', reached ? 0.8 : 0.28);
      ctx.fillRect(
        tickX - HP_TICK_THICKNESS_PX / 2,
        HP_BAR_TOP_PX,
        HP_TICK_THICKNESS_PX,
        HP_BAR_HEIGHT_PX,
      );
    }

    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    const scoreScale = 1 + this.comboPulseT * 0.08;
    const scoreSize = Math.round(28 * scoreScale);
    ctx.font = `bold ${scoreSize}px system-ui, sans-serif`;
    const scoreText = Math.round(displayedScore).toLocaleString();
    const scoreY = HP_BAR_TOP_PX + HP_BAR_HEIGHT_PX + 6;
    const hudRightX = barX + barW;
    // Chromatic-split aberration on high-pulse frames so a fresh
    // combo "kerchunks" the score readout — cyan + magenta ghosts
    // offset a few px each side, white on top. Skipped under reduced
    // motion (the pulse scale already telegraphs the increment) and
    // on low tier (extra fillText pair + state churn per pulse adds
    // up across hot streaks).
    const allowChromaticGhost = !reducedMotion && quality.chromeShadows;
    if (allowChromaticGhost && this.comboPulseT > 0.25) {
      const offset = this.comboPulseT * 2.4;
      ctx.save();
      ctx.globalAlpha = 0.55 * this.comboPulseT;
      ctx.fillStyle = '#3bf0ff';
      ctx.fillText(scoreText, hudRightX - offset, scoreY);
      ctx.fillStyle = '#ff3bdc';
      ctx.fillText(scoreText, hudRightX + offset, scoreY);
      ctx.restore();
    }
    ctx.fillStyle = palette.ui;
    if (!reducedMotion && this.comboPulseT > 0 && quality.chromeShadows) {
      ctx.save();
      ctx.shadowColor = '#ffd84d';
      ctx.shadowBlur = this.comboPulseT * 20;
      ctx.fillText(scoreText, hudRightX, scoreY);
      ctx.restore();
    } else {
      ctx.fillText(scoreText, hudRightX, scoreY);
    }

    if (state.combo > 1) {
      this.drawComboReadout(
        ctx,
        state.combo,
        scoreY,
        scoreSize,
        hudRightX,
        reducedMotion,
        quality,
      );
    }
  }

  private drawComboReadout(
    ctx: CanvasRenderingContext2D,
    combo: number,
    scoreY: number,
    scoreSize: number,
    hudRightX: number,
    reducedMotion: boolean,
    quality: RenderQuality,
  ): void {
    const tier = comboTier(combo);
    const tierColour = COMBO_BLOOM_PALETTE[tier];
    const comboScale = 1 + this.comboPulseT * 0.22;
    const comboSize = Math.round(COMBO_HUD_BASE_FONT_PX * comboScale);
    const comboY = scoreY + scoreSize + 10;
    ctx.font = `bold ${comboSize}px system-ui, sans-serif`;
    ctx.fillStyle = tierColour;
    const comboLabel = `${combo}x`;
    const allowGlow = !reducedMotion && quality.chromeShadows;
    const baseGlow = allowGlow && tier > 0 ? 4 + tier * 4 : 0;
    const pulseGlow = allowGlow ? this.comboPulseT * 16 : 0;
    const totalGlow = baseGlow + pulseGlow;
    // Kerning bounce: rotate the readout slightly during the pulse so
    // the number "snaps" into place on each fresh combo increment.
    // Tiny angle — large rotations would smear the text — but enough
    // to land a satisfying micro-impact.
    const wobbleAngle =
      allowGlow && this.comboPulseT > 0
        ? Math.sin(performance.now() * 0.025) * this.comboPulseT * 0.05
        : 0;
    const needsRotate = wobbleAngle !== 0;
    if (needsRotate) {
      ctx.save();
      ctx.translate(hudRightX, comboY + comboSize / 2);
      ctx.rotate(wobbleAngle);
      ctx.translate(-hudRightX, -(comboY + comboSize / 2));
    }
    // Chromatic-split ghosts on big pulses so a fresh combo "punches"
    // the readout the same way it does for the score, sized larger
    // because the combo is the higher-energy readout. Skipped on
    // low tier alongside the shadow glow (`allowGlow` already gates
    // chromeShadows for the rest of this readout).
    if (allowGlow && this.comboPulseT > 0.25) {
      const offset = this.comboPulseT * 3.2;
      ctx.save();
      ctx.globalAlpha = 0.6 * this.comboPulseT;
      ctx.fillStyle = '#3bf0ff';
      ctx.fillText(comboLabel, hudRightX - offset, comboY);
      ctx.fillStyle = '#ff3bdc';
      ctx.fillText(comboLabel, hudRightX + offset, comboY);
      ctx.restore();
    }
    ctx.fillStyle = tierColour;
    if (totalGlow > 0) {
      ctx.save();
      ctx.shadowColor = tierColour;
      ctx.shadowBlur = totalGlow;
      ctx.fillText(comboLabel, hudRightX, comboY);
      ctx.restore();
    } else {
      ctx.fillText(comboLabel, hudRightX, comboY);
    }
    if (needsRotate) {
      ctx.restore();
    }

    if (combo >= COMBO_UNDERLINE_MIN) {
      const labelW = ctx.measureText(comboLabel).width;
      const underlineFrac = Math.min(1, combo / 100);
      const underlineW = labelW * (0.3 + 0.7 * underlineFrac);
      const underlineH = 2 + tier;
      const underlineY = comboY + comboSize + 4;
      if (allowGlow && tier > 0) {
        ctx.save();
        ctx.shadowColor = tierColour;
        ctx.shadowBlur = 4 + tier * 3;
        ctx.fillStyle = withAlpha(tierColour, 0.85);
        ctx.fillRect(hudRightX - underlineW, underlineY, underlineW, underlineH);
        ctx.restore();
      } else {
        ctx.fillStyle = withAlpha(tierColour, 0.85);
        ctx.fillRect(hudRightX - underlineW, underlineY, underlineW, underlineH);
      }
    }
  }
}
